import { Link } from "react-router-dom";
import { useTranslation } from "@/context/TranslationContext";

const serviceLinks = [
  { key: "saas", labelKey: "footer.service_saas", href: "/services" },
  { key: "web_mobile", labelKey: "footer.service_web_mobile", href: "/services" },
  { key: "automation", labelKey: "footer.service_automation", href: "/services" },
  { key: "fintech", labelKey: "footer.service_fintech", href: "/services" },
  { key: "trading", labelKey: "footer.service_trading", href: "/services" },
  { key: "ai", labelKey: "footer.service_ai", href: "/services" },
];

const companyLinks = [
  { key: "services", labelKey: "footer.company_services", href: "/services" },
  { key: "portfolio", labelKey: "footer.company_portfolio", href: "/portfolio" },
  { key: "pricing", labelKey: "footer.company_pricing", href: "/pricing" },
  { key: "about", labelKey: "footer.company_about", href: "/about" },
  { key: "contact", labelKey: "footer.company_contact", href: "/contact" },
];

const socials = [
  {
    icon: "ri-tiktok-line",
    href: "https://www.tiktok.com/@schemackozoo?_r=1&_t=ZG-96rTQe5mrth",
    label: "TikTok",
  },
  {
    icon: "ri-linkedin-line",
    href: "https://www.linkedin.com/company/schemackozo/",
    label: "LinkedIn",
  },
  {
    icon: "ri-facebook-line",
    href: "https://www.facebook.com/share/1D6sjS4n7E/?mibextid=wwXIfr",
    label: "Facebook",
  },
  {
    icon: "ri-instagram-line",
    href: "https://www.instagram.com/schemackozo?igsh=MWM5NGh5amFpZGc3MA%3D%3D&utm_source=qr",
    label: "Instagram",
  },
];

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="bg-navy-dark border-t border-dark-border">
      {/* Top CTA strip */}
      <div className="border-b border-dark-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-black text-xl mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("footer.cta_title")}
            </p>
            <p className="text-silver-dim text-sm">{t("footer.cta_subtitle")}</p>
          </div>
          <Link
            to="/contact"
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-7 py-3 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            {t("footer.cta_button")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png"
                alt="Schemackozo"
                className="h-10 w-auto"
              />
              <span className="font-black text-white text-sm tracking-widest" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.12em" }}>
                SCHEMACKOZO
              </span>
            </Link>
            <p className="text-silver text-sm leading-relaxed mb-6">
              {t("footer.description")}
            </p>
            <div className="flex items-center gap-2.5">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-dark-border text-silver-dim hover:text-electric-blue hover:border-electric-blue/40 transition-all duration-200 cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-xs uppercase tracking-[0.15em] mb-5">
              <a href="/services">{t("footer.services")}</a>
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((s) => (
                <li key={s.key}>
                  <Link
                    to={s.href}
                    className="text-silver-dim text-sm hover:text-electric-blue transition-colors cursor-pointer"
                  >
                    {t(s.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-bold text-xs uppercase tracking-[0.15em] mb-5">{t("footer.company")}</h4>
            <ul className="space-y-3">
              {companyLinks.map((c) => (
                <li key={c.key}>
                  <Link
                    to={c.href}
                    className="text-silver-dim text-sm hover:text-electric-blue transition-colors cursor-pointer"
                  >
                    {t(c.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-xs uppercase tracking-[0.15em] mb-5">{t("footer.get_in_touch")}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-map-pin-line text-electric-blue text-sm" />
                </div>
                <span className="text-silver-dim text-sm leading-relaxed">
                  {t("footer.location")}<br />{t("footer.location_sub")}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-electric-blue text-sm" />
                </div>
                <a href="mailto:info@schemackozo.com" className="text-silver-dim text-sm hover:text-electric-blue transition-colors cursor-pointer">
                  info@schemackozo.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-electric-blue text-sm" />
                </div>
                <span className="text-silver-dim text-sm">{t("footer.hours")}</span>
              </li>
            </ul>

            {/* Status badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyber-green/20 bg-cyber-green/5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyber-green animate-glow-pulse" />
              <span className="text-cyber-green text-xs font-medium">{t("footer.accepting_projects")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-silver-dim text-xs">
            &copy; {new Date().getFullYear()} Schemackozo. {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-5">
            <a href="/privacy" rel="nofollow" className="text-silver-dim text-xs hover:text-electric-blue transition-colors cursor-pointer">{t("footer.privacy")}</a>
            <a href="/terms" rel="nofollow" className="text-silver-dim text-xs hover:text-electric-blue transition-colors cursor-pointer">{t("footer.terms")}</a>
            <a href="/sitemap.xml" rel="nofollow" className="text-silver-dim text-xs hover:text-electric-blue transition-colors cursor-pointer">{t("footer.sitemap")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
